import React from 'react';
import TdButton from '../Button';
import { cn } from '../model/derive';

/** Growth Report 섹션 블록 렌더러 — reportModel 의 블록 종류마다 하나 */

const Label = ({ children }) => (
  <span className='text-[13px] font-medium leading-[140%] text-text-secondary'>{children}</span>
);

const VERDICT_TONE = {
  Above: 'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]',
  Meets: 'bg-surface-green10 text-green-deep04',
  Below: 'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]',
  'You underrate this': 'bg-functional-accent01 text-functional-accent04',
  'You overrate this': 'bg-functional-warning01 text-functional-warning04',
  'Spot on': 'bg-surface-green10 text-green-deep04',
  'Exceeds expectations':
    'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]',
  'Meets expectations': 'bg-surface-green10 text-green-deep04',
  'Below expectations':
    'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]',
};
const Verdict = ({ children }) =>
  children ? (
    <span
      className={cn(
        'inline-flex h-[22px] items-center whitespace-nowrap rounded-md px-2 text-[12px] font-medium',
        VERDICT_TONE[children] ?? 'bg-grayscale-gray01 text-text-secondary',
      )}
    >
      {children}
    </span>
  ) : null;

const Bars = ({ rows, max }) => (
  <div className='flex flex-col'>
    {rows.map((r, i) => (
      <div
        key={r.label}
        className={cn(
          'grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_56px] items-center gap-x-4 gap-y-2 py-3 max-md:grid-cols-[minmax(0,1fr)_56px]',
          i > 0 && 'border-t border-green-deep03/[0.06]',
        )}
      >
        <div className='flex min-w-0 flex-col gap-0.5'>
          <span className='text-[14px] font-medium leading-[140%]'>{r.label}</span>
          <span className='text-[12px] leading-[140%] text-text-secondary'>{r.sub}</span>
        </div>
        <div className='relative h-2 rounded bg-green-deep03/[0.08] max-md:col-span-2 max-md:row-start-2'>
          <span
            className='absolute inset-y-0 left-0 rounded bg-green-deep02'
            style={{ width: `${Math.round((r.value / max) * 100)}%` }}
          />
        </div>
        <span className='text-right text-[16px] font-medium tabular-nums'>
          {r.value.toFixed(1)}
        </span>
      </div>
    ))}
  </div>
);

const Strengths = ({ title, items }) => (
  <div className='flex flex-col gap-2'>
    <Label>{title}</Label>
    <div className='flex flex-wrap gap-2'>
      {items.map(([label, n]) => (
        <span
          key={label}
          className='inline-flex h-8 items-center gap-1.5 rounded-full bg-surface-green10 px-3 text-[13px] font-medium text-green-deep04'
        >
          {label}
          <span className='text-text-secondary'>×{n}</span>
        </span>
      ))}
    </div>
  </div>
);

const Total = ({ title, value, note }) => (
  <div className='flex flex-col gap-1 rounded-xl bg-green-deep03/[0.04] px-5 py-4'>
    <span className='text-[13px] text-text-secondary'>{title}</span>
    <span className='text-[24px] font-medium leading-[120%] tabular-nums'>{value}</span>
    {note && <span className='text-[12px] text-text-tertiary'>{note}</span>}
  </div>
);

const Paragraphs = ({ items }) => (
  <div className='flex flex-col gap-3'>
    {items.map(([title, text]) => (
      <div key={title} className='flex flex-col gap-1'>
        <Label>{title}</Label>
        <p className='text-[14px] leading-[160%] [text-wrap:pretty]'>{text}</p>
      </div>
    ))}
  </div>
);

/** 테두리 상자 안 줄 목록 — Kit 항목 · 루브릭 */
const Boxed = ({ children }) => (
  <div className='flex flex-col rounded-xl border border-green-deep03/[0.1]'>{children}</div>
);
const BoxRow = ({ children }) => (
  <div className='flex flex-wrap items-start justify-between gap-3 border-t border-green-deep03/[0.08] px-5 py-4 first:border-t-0'>
    {children}
  </div>
);

const Quotes = ({ items }) => (
  <Boxed>
    {items.map(([quote, source]) => (
      <BoxRow key={quote}>
        <span className='flex min-w-0 flex-1 flex-col gap-1'>
          <span className='text-[15px] leading-[150%]'>{quote}</span>
          <span className='text-[13px] text-text-secondary'>{source}</span>
        </span>
      </BoxRow>
    ))}
  </Boxed>
);

/** 관찰값 막대 — 채움 = 관찰 비율, 세로 눈금 = 기준치(또는 자가진단 점수) */
const MeterBar = ({ pct, mark }) => (
  <div className='relative h-2 rounded bg-green-deep03/[0.08]'>
    <span
      className='absolute inset-y-0 left-0 rounded bg-green-deep02'
      style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
    />
    {mark != null && (
      <span
        aria-hidden
        className='absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded bg-text-primary'
        style={{ left: `${Math.max(0, Math.min(100, mark))}%` }}
      />
    )}
  </div>
);

const AxisRow = ({ r, right }) => (
  <div className='flex flex-col gap-2.5 border-t border-green-deep03/[0.08] py-4 first:border-t-0 first:pt-1'>
    <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1'>
      <span className='text-[15px] font-medium'>{r.axis}</span>
      <span className='flex flex-wrap items-center justify-end gap-2'>
        <span className='text-[15px] font-medium tabular-nums'>{r.value}</span>
        {right}
      </span>
    </div>
    {r.pct != null && <MeterBar pct={r.pct} mark={r.mark} />}
    {(r.notes ?? []).length > 0 && (
      <div className='flex flex-col gap-1'>
        {r.notes.map((n, i) => (
          <span
            key={n}
            className={cn(
              'leading-[140%]',
              i === 0 && r.notes.length > 1
                ? 'text-[14px] text-text-primary'
                : 'text-[13px] text-text-secondary',
            )}
          >
            {n}
          </span>
        ))}
      </div>
    )}
    {r.teammates && (
      <span className='w-fit rounded-md bg-grayscale-gray01 px-2.5 py-1 text-[13px] leading-[140%] text-text-secondary'>
        {r.teammates}
      </span>
    )}
  </div>
);

const Axes = ({ match, strengths, gaps, rows, label }) => (
  <div className='flex flex-col gap-4'>
    <div className='grid gap-3 sm:grid-cols-3'>
      <div className='flex flex-col gap-1 rounded-xl bg-green-deep03/[0.04] px-4 py-3'>
        <Label>Match</Label>
        <span className='text-[24px] font-medium leading-[120%]'>{match.value}</span>
        <span className='text-[12px] text-text-secondary'>{match.sub}</span>
      </div>
      <div className='flex flex-col gap-1 rounded-xl bg-green-deep03/[0.04] px-4 py-3'>
        <Label>Top strengths</Label>
        <span className='text-[20px] font-medium leading-[130%]'>{strengths}</span>
      </div>
      <div className='flex flex-col gap-1 rounded-xl bg-green-deep03/[0.04] px-4 py-3'>
        <Label>Top gaps</Label>
        <span className='text-[20px] font-medium leading-[130%]'>{gaps}</span>
      </div>
    </div>
    <div>
      {rows.map((r) => (
        <AxisRow
          key={r.axis}
          r={r}
          right={
            <>
              {r.benchmark && (
                <span className='text-[13px] text-text-secondary'>{r.benchmark}</span>
              )}
              <Verdict>{r.verdict}</Verdict>
            </>
          }
        />
      ))}
    </div>
    <p className='text-[12px] text-text-tertiary'>{label}</p>
  </div>
);

const Self = ({ rows }) => (
  <div>
    {rows.map((r) => (
      <AxisRow
        key={r.axis}
        r={r}
        right={
          <>
            <span className='text-[13px] text-text-secondary'>{r.self}</span>
            <Verdict>{r.verdict}</Verdict>
          </>
        }
      />
    ))}
  </div>
);

const Actions = ({ title, items }) => (
  <div className='flex flex-col gap-2'>
    <Label>{title}</Label>
    <ol className='flex flex-col gap-2'>
      {items.map(([text, tag], i) => (
        <li key={text} className='flex gap-3 rounded-xl bg-green-deep03/[0.04] px-4 py-3'>
          <span className='text-[13px] font-medium text-green-deep04'>{i + 1}</span>
          <span className='flex min-w-0 flex-1 flex-col gap-1'>
            <span className='text-[14px] leading-[150%]'>{text}</span>
            <span className='text-[12px] text-text-secondary'>{tag}</span>
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const Items = ({ title, items }) => (
  <div className='flex flex-col gap-1'>
    {title && <Label>{title}</Label>}
    <ul>
      {items.map(([head, sub]) => (
        <li
          key={head}
          className='flex flex-col gap-0.5 border-t border-green-deep03/[0.06] py-2.5 first:border-t-0'
        >
          <span className='text-[14px] font-medium leading-[140%]'>{head}</span>
          <span className='text-[12px] leading-[150%] text-text-secondary'>{sub}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Chips = ({ title, items }) => (
  <div className='flex flex-col gap-2'>
    <Label>{title}</Label>
    <div className='flex flex-wrap gap-2'>
      {items.map((t) => (
        <span
          key={t}
          className='inline-flex h-8 items-center rounded-full bg-grayscale-gray01 px-3 text-[13px] text-text-primary shadow-[inset_0_0_0_1px_#e4e1dc]'
        >
          {t}
        </span>
      ))}
    </div>
  </div>
);

const Stats = ({ items }) => (
  <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
    {items.map(([label, value, sub]) => (
      <div
        key={label}
        className='flex flex-col gap-0.5 rounded-xl bg-green-deep03/[0.04] px-4 py-3'
      >
        <span className='text-[12px] text-text-secondary'>{label}</span>
        <span className='text-[20px] font-medium tabular-nums'>{value}</span>
        {sub && <span className='text-[12px] text-text-secondary'>{sub}</span>}
      </div>
    ))}
  </div>
);

const Rubric = ({ rows }) => (
  <Boxed>
    {rows.map(([criterion, desc, level]) => (
      <BoxRow key={criterion}>
        <span className='flex min-w-0 flex-1 flex-col gap-1'>
          <span className='text-[15px] font-medium'>{criterion}</span>
          <span className='text-[13px] leading-[150%] text-text-secondary'>{desc}</span>
        </span>
        <Verdict>{level}</Verdict>
      </BoxRow>
    ))}
  </Boxed>
);

const QA = ({ items }) => <Paragraphs items={items} />;

const Note = ({ text }) => <p className='text-[14px] text-text-secondary'>{text}</p>;

/** AI 초안 자리 — 작성 중 · 실패(Retry) */
const AiPending = ({ state, onRetry }) =>
  state === 'error' ? (
    <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl bg-functional-failed01 px-4 py-3'>
      <span className='text-[14px] text-functional-failed04'>Couldn’t write the AI draft.</span>
      {onRetry && (
        <TdButton
          variant='soft'
          onClick={onRetry}
          className='bg-grayscale-gray00 px-4 text-[13px] shadow-4 print:hidden'
        >
          Retry
        </TdButton>
      )}
    </div>
  ) : (
    <div className='flex flex-col gap-2.5' aria-busy='true'>
      <span className='text-[13px] text-text-secondary'>
        {state === 'loading'
          ? 'Writing the AI draft… this can take up to a minute.'
          : 'AI draft not written yet.'}
      </span>
      <div className='flex animate-pulse flex-col gap-2.5'>
        {[92, 76, 84].map((w) => (
          <span
            key={w}
            className='h-3.5 rounded-full bg-green-deep03/[0.08]'
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );

const RENDER = {
  bars: Bars,
  strengths: Strengths,
  total: Total,
  paragraphs: Paragraphs,
  qa: QA,
  quotes: Quotes,
  axes: Axes,
  self: Self,
  actions: Actions,
  items: Items,
  chips: Chips,
  stats: Stats,
  rubric: Rubric,
  note: Note,
  aiPending: AiPending,
};

export const ReportBlock = ({ block }) => {
  const C = RENDER[block.type];
  return C ? <C {...block} /> : null;
};

/** Free 보기 자리표시 — 값이 없으니 회색 줄을 흐리게 */
export const LockedLines = ({ lines }) => (
  <div aria-hidden className='flex select-none flex-col gap-2.5 blur-[3px]'>
    {Array.from({ length: lines }, (_, i) => (
      <span
        key={i}
        className='h-3.5 rounded-full bg-green-deep03/[0.1]'
        style={{ width: `${[92, 76, 84, 60][i % 4]}%` }}
      />
    ))}
  </div>
);
